import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Edit, Trash2, Users2, User as UserIcon, ArrowRight, Calendar, CheckCircle2, Shield } from 'lucide-react';
import { TechnicalTeam } from '../../types';
import { useForm, SubmitHandler } from 'react-hook-form';
import useAppStore from '../../store/useAppStore';
import toast from 'react-hot-toast';
import { EmptyState } from '../../components/common/EmptyState';
import { useAppContext } from '../../hooks/useAppContext';

type TeamFormInputs = {
    name: string;
    leaderId: string;
    members: string; // Will be a newline-separated string from textarea
};

const ModernTeamFormModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    team: TechnicalTeam | null;
    onSave: (data: any) => Promise<void>;
    isSaving: boolean;
}> = ({ isOpen, onClose, team, onSave, isSaving }) => {
    const { users } = useAppStore();
    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<TeamFormInputs>();

    React.useEffect(() => {
        if (isOpen) {
            if (team) {
                reset({
                    name: team.name,
                    leaderId: team.leaderId,
                    members: team.members.join('\n'),
                });
            } else {
                reset({ name: '', leaderId: users[0]?.id || '', members: '' });
            }
        }
    }, [team, isOpen, reset, users]);

    const onSubmit: SubmitHandler<TeamFormInputs> = data => {
        const memberArray = data.members.split('\n').map(m => m.trim()).filter(m => m);
        if (team) {
            onSave({ ...data, members: memberArray, id: team.id });
        } else {
            onSave({ ...data, members: memberArray });
        }
    };

    const memberCount = useMemo(() => {
        const membersValue = watch('members');
        if (!membersValue) return 0;
        return membersValue.split('\n').filter(m => m.trim()).length;
    }, [watch('members')]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="glass-panel w-full max-w-lg p-6 animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="flex justify-between items-center border-b border-white/10 pb-4">
                        <h2 className="text-xl font-bold text-white">{team ? 'تعديل الفريق' : 'إضافة فريق فني جديد'}</h2>
                        <button type="button" onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                            <span className="sr-only">Close</span>
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-slate-300 mb-2 block">اسم الفريق</label>
                            <div className="relative">
                                <Users2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                <input
                                    {...register("name", { required: "اسم الفريق مطلوب" })}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-10 pl-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="اسم الفريق"
                                />
                            </div>
                            {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name.message}</p>}
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-300 mb-2 block">قائد الفريق</label>
                            <div className="relative">
                                <Shield className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                                <select
                                    {...register("leaderId", { required: "يجب اختيار قائد للفريق" })}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-10 pl-4 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none"
                                >
                                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </div>
                            {errors.leaderId && <p className="text-xs text-red-400 mt-1">{errors.leaderId.message}</p>}
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-300 mb-2 block">أعضاء الفريق (اسم واحد في كل سطر)</label>
                            <textarea
                                {...register("members")}
                                rows={5}
                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 px-4 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                                placeholder={`مثال:\nأحمد عبدالله\nخالد محمد`}
                            />
                            <p className="text-xs text-slate-400 mt-1">عدد الأعضاء: {memberCount}</p>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSaving}
                            className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5 transition-colors"
                        >
                            إلغاء
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-6 py-2 rounded-lg bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:shadow-primary/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSaving && <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                            <span>حفظ</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ModernManageTeamsScreen: React.FC = () => {
    const { t } = useAppContext();
    const { technicalTeams, addTechnicalTeam, updateTechnicalTeam, deleteTechnicalTeam, openConfirmation } = useAppStore();
    const navigate = useNavigate();

    const [isModalOpen, setModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingTeam, setEditingTeam] = useState<TechnicalTeam | null>(null);

    const handleSaveTeam = async (data: any) => {
        setIsSaving(true);
        try {
            if (editingTeam) {
                await updateTechnicalTeam(data);
                toast.success('تم تحديث الفريق بنجاح!');
            } else {
                await addTechnicalTeam(data);
                toast.success('تم إنشاء الفريق بنجاح!');
            }
            setModalOpen(false);
            setEditingTeam(null);
        } catch (error) {
            // Errors are already toasted in the store
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddClick = () => {
        setEditingTeam(null);
        setModalOpen(true);
    };

    const handleEditClick = (team: TechnicalTeam) => {
        setEditingTeam(team);
        setModalOpen(true);
    };

    const handleDeleteClick = (teamId: string) => {
        openConfirmation('هل أنت متأكد من حذف هذا الفريق؟', async () => {
            try {
                await deleteTechnicalTeam(teamId);
                toast.success('تم حذف الفريق بنجاح!');
            } catch (error) {
                // Errors are already toasted in the store
            }
        });
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">إدارة الفرق الفنية</h1>
                    <p className="text-slate-400">إدارة فرق العمل وتوزيع المهام</p>
                </div>
                <div className="flex items-center gap-3 w-full md:w-auto">
                    <button
                        onClick={() => navigate('/')}
                        className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                    >
                        <ArrowRight size={18} />
                        <span>رجوع</span>
                    </button>
                    <button
                        onClick={handleAddClick}
                        className="px-6 py-2 rounded-xl bg-gradient-to-r from-primary to-purple-600 text-white font-medium hover:shadow-lg hover:shadow-primary/25 transition-all flex items-center gap-2"
                    >
                        <PlusCircle size={18} />
                        <span>إضافة فريق</span>
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">إجمالي الفرق</p>
                        <p className="text-3xl font-bold text-white">{technicalTeams.length}</p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-primary/20 flex items-center justify-center text-primary">
                        <Users2 size={24} />
                    </div>
                </div>
                <div className="glass-card p-6 rounded-2xl flex items-center justify-between">
                    <div>
                        <p className="text-slate-400 text-sm mb-1">إجمالي الأعضاء</p>
                        <p className="text-3xl font-bold text-white">
                            {technicalTeams.reduce((acc, team) => acc + team.members.length, 0)}
                        </p>
                    </div>
                    <div className="h-12 w-12 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400">
                        <UserIcon size={24} />
                    </div>
                </div>
            </div>

            {/* Teams Grid */}
            {technicalTeams.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {technicalTeams.map((team) => (
                        <div key={team.id} className="glass-card p-6 rounded-2xl group hover:border-primary/30 transition-all duration-300">
                            <div className="flex justify-between items-start mb-4">
                                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-white/5 flex items-center justify-center text-slate-300 group-hover:text-primary group-hover:border-primary/30 transition-colors">
                                    <Users2 size={24} />
                                </div>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                        onClick={() => handleEditClick(team)}
                                        className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                                    >
                                        <Edit size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(team.id)}
                                        className="p-2 rounded-lg hover:bg-red-500/10 text-slate-400 hover:text-red-400 transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            <h3 className="text-xl font-bold text-white mb-4">{team.name}</h3>

                            <div className="space-y-3">
                                <div className="flex items-center gap-3 text-slate-400">
                                    <Shield size={16} className="text-purple-400/70" />
                                    <span className="text-sm">القائد: {team.leaderName}</span>
                                </div>
                                <div className="flex items-center gap-3 text-slate-400">
                                    <UserIcon size={16} className="text-blue-400/70" />
                                    <span className="text-sm">{team.members.length} أعضاء</span>
                                </div>
                                <div className="flex items-center gap-3 text-slate-400 pt-2 border-t border-white/5 mt-2">
                                    <Calendar size={16} className="text-slate-500" />
                                    <span className="text-xs">تاريخ الإنشاء: {new Date(team.creationDate).toLocaleDateString('ar-SA')}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <EmptyState
                    icon={Users2}
                    title="لا توجد فرق فنية"
                    message="لم يتم إنشاء أي فريق حتى الآن. ابدأ بإنشاء فريق جديد."
                    action={<button onClick={handleAddClick} className="px-6 py-2 rounded-xl bg-primary text-white font-medium hover:shadow-lg hover:shadow-primary/25 transition-all flex items-center gap-2"><PlusCircle size={18} /><span>إضافة فريق</span></button>}
                />
            )}

            <ModernTeamFormModal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                team={editingTeam}
                onSave={handleSaveTeam}
                isSaving={isSaving}
            />
        </div>
    );
};

export default ModernManageTeamsScreen;
